import React, { useEffect, useMemo, useState } from "react";
import { api, fmtEUR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Home, Calendar, Clock, Coins, BarChart3, MessageSquare, Headphones,
  Bell, LogOut, Sun, Moon, Bed, ChevronLeft, ChevronRight, ChevronDown,
  ArrowRight, Info, Car, Activity, Scale, User as UserIcon, Send, CheckCheck, Check,
  Fuel, AlertTriangle, Route, Wrench, Receipt, Phone, Plane, TrainFront, MoreHorizontal, CalendarDays, Crown
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DriverHome } from "../components/driver/DriverHome";
import { DriverOffice } from "../components/driver-requests/DriverOffice";
import { ShiftCalendar } from "../components/driver/ShiftCalendar";
import PushNotificationsCard from "../components/PushNotificationsCard";
import DriverNotificationsBell from "../components/DriverNotificationsPanel";
import ChangePasswordButton from "../components/ChangePasswordDialog";
import { DriverVehicleCheck } from "../components/vehicle-check/DriverVehicleCheck";
import { GpsTracker } from "../components/gps/GpsTracker";
import { DriverRecognition } from "../components/driver/DriverRecognition";
import { DriverCopilot } from "../components/driver/DriverCopilot";
import { ServiceProgressPanel } from "../components/service-progress/ServiceProgressPanel";
import { DispoDriverPanel } from "../components/dispo/DispoDriverPanel";
import { isDispo } from "../components/dispo/dispoUtils";

const MONTHS_IT = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const DAYS_SHORT = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];
const DAYS_MINI = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];

const TABS = [
  { key: "home", label: "Home", icon: Home },
  { key: "servizi", label: "Servizi", icon: Calendar },
  { key: "turni", label: "Turnistica", icon: CalendarDays },
  { key: "conteggi", label: "Conteggi", icon: Coins },
  { key: "uber", label: "Uber", icon: BarChart3 },
  { key: "altro", label: "Altro", icon: MoreHorizontal },
];
const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function Brand() {
  return (
    <div className="flex items-center gap-2.5" data-testid="driver-brand">
      <Crown size={18} className="text-[#E9C46A]" />
      <div className="leading-none"><div className="font-display text-[17px] font-bold tracking-wide text-[#E9C46A]">STRIANO NCC</div><div className="text-[9px] uppercase tracking-[0.35em] text-[#9CC4FF] mt-1">Area Driver</div></div>
    </div>
  );
}

function ServicesTab({ bookings, onOpen, onMonth, monthCount }) {
  const [sub, setSub] = useState("oggi");
  const t = new Date(); const today = isoLocal(t); t.setDate(t.getDate() + 1); const tomorrow = isoLocal(t);
  const list = bookings.map((b, i) => ({ b, i })).filter(({ b }) => sub === "tutti" || b.date === (sub === "oggi" ? today : tomorrow));
  const count = (k) => k === "tutti" ? bookings.length : bookings.filter(b => b.date === (k === "oggi" ? today : tomorrow)).length;
  return (
    <section className="rounded-2xl border border-[#2F7BFF]/25 bg-[#081226] p-4 sm:p-5 space-y-3" id="prenotazioni" data-testid="driver-services-section">
      <h3 className="font-display text-xl font-bold text-white">I miei servizi</h3>
      <div className="grid grid-cols-3 gap-1 p-1 rounded-xl bg-black/40 border border-white/10">
        {[["oggi", "Oggi"], ["domani", "Domani"], ["tutti", "Tutti"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setSub(k)} data-testid={`driver-services-tab-${k}`} className={`min-h-[40px] rounded-lg text-sm font-bold ${sub === k ? "bg-[#1F6BFF] text-white shadow-[0_0_14px_rgba(31,107,255,0.55)]" : "text-slate-400"}`}>{l} <span className="opacity-70 font-mono-num">{count(k)}</span></button>
        ))}
      </div>
      <div className="space-y-3" data-testid="driver-services-list">
        {list.length === 0 && <div className="py-8 text-center text-slate-500 text-sm" data-testid="driver-services-empty">Nessun servizio {sub === "oggi" ? "per oggi" : sub === "domani" ? "per domani" : "assegnato"}</div>}
        {list.map(({ b, i }) => <DriverServiceCard key={b.id} b={b} onOpen={() => onOpen(i)} />)}
      </div>
      <button type="button" onClick={onMonth} data-testid="driver-services-month" className="w-full min-h-[44px] rounded-xl border border-[#E9C46A]/40 text-[#E9C46A] text-xs font-bold uppercase tracking-widest">Tutti i servizi del mese ({monthCount})</button>
    </section>
  );
}

const STATUS_STYLE = {
  "Confermata":   { dot:"bg-emerald-400", label:"Confermata" },
  "In corso":     { dot:"bg-sky-400",     label:"In corso" },
  "In attesa":    { dot:"bg-amber-400",   label:"In attesa" },
  "Programmata":  { dot:"bg-slate-400",   label:"Programmata" },
  "Cancellata":   { dot:"bg-rose-500",    label:"Cancellata" },
  "Completata":   { dot:"bg-emerald-400", label:"Completata" },
};

function greeting() {
  const h = new Date().getHours();
  if (h < 5) return "Buonanotte";
  if (h < 12) return "Buongiorno";
  if (h < 18) return "Buon pomeriggio";
  return "Buonasera";
}

function fmtDateFull() {
  const d = new Date();
  const gg = ["Domenica","Lunedì","Martedì","Mercoledì","Giovedì","Venerdì","Sabato"][d.getDay()];
  return `${gg} ${String(d.getDate()).padStart(2,"0")} ${MONTHS_IT[d.getMonth()]} ${d.getFullYear()}`;
}

export default function DriverArea() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthModal, setMonthModal] = useState(false);
  const [showServicesList, setShowServicesList] = useState(false);
  const [upSel, setUpSel] = useState(null);
  const [calCursor, setCalCursor] = useState(() => {
    const d = new Date(); d.setDate(1); return d;
  });
  // === Periodo mensile selezionato (storico driver) ===
  const _now = new Date();
  const [selYear, setSelYear] = useState(_now.getFullYear());
  const [selMonth, setSelMonth] = useState(_now.getMonth() + 1);

  const load = async () => {
    try {
      const r = await api.get("/driver/me/dashboard", { params: { year: selYear, month: selMonth } });
      setData(r.data);
    } catch (e) {
      toast.error("Impossibile caricare l'area driver");
    }
  };
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); // eslint-disable-next-line
  }, [selYear, selMonth]);

  const goPrevMonth = () => {
    let y = selYear, m = selMonth - 1;
    if (m < 1) { m = 12; y -= 1; }
    setSelYear(y); setSelMonth(m);
  };
  const goNextMonth = () => {
    let y = selYear, m = selMonth + 1;
    if (m > 12) { m = 1; y += 1; }
    const todayY = _now.getFullYear(); const todayM = _now.getMonth() + 1;
    if (y > todayY || (y === todayY && m > todayM)) return; // no future
    setSelYear(y); setSelMonth(m);
  };
  const isCurrentPeriod = selYear === _now.getFullYear() && selMonth === _now.getMonth() + 1;

  const firstName = data?.driver?.first_name || user?.name?.split(" ")[0] || "Autista";
  const fullName = data ? `${data.driver.first_name || ""} ${data.driver.last_name || ""}`.trim() : (user?.name || "Autista");

  const weekDays = useMemo(() => {
    if (!data) return [];
    const start = new Date(data.week_start);
    start.setDate(start.getDate() + weekOffset * 7);
    const arr = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start); d.setDate(start.getDate() + i);
      // LOCAL date-only ISO to avoid UTC shift (Italy = UTC+1/+2)
      const iso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
      const shift = (data.shifts_week || []).find(s => s.date === iso);
      arr.push({ date: d, iso, shift, dayLabel: DAYS_SHORT[i] });
    }
    return arr;
  }, [data, weekOffset]);

  const miniCalendar = useMemo(() => {
    const cur = new Date(calCursor);
    const y = cur.getFullYear(); const m = cur.getMonth();
    const first = new Date(y, m, 1);
    const startDow = (first.getDay() + 6) % 7; // Monday=0
    const daysInMonth = new Date(y, m+1, 0).getDate();
    const daysPrev = new Date(y, m, 0).getDate();
    const cells = [];
    for (let i = startDow - 1; i >= 0; i--) {
      const d = daysPrev - i;
      cells.push({ day: d, cur: false, iso: `${y}-${String(m).padStart(2,"0")}-${String(d).padStart(2,"0")}` });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      cells.push({ day: i, cur: true, iso: `${y}-${String(m+1).padStart(2,"0")}-${String(i).padStart(2,"0")}` });
    }
    while (cells.length % 7 !== 0 || cells.length < 42) {
      cells.push({ day: cells.length - (startDow + daysInMonth) + 1, cur: false });
      if (cells.length >= 42) break;
    }
    return { y, m, cells: cells.slice(0, 42) };
  }, [calCursor]);

  const todayIso = new Date().toISOString().slice(0,10);

  const sidebarItems = [
    { key:"dash", icon:Home, label:"La mia dashboard", anchor:"#top" },
    { key:"book", icon:Calendar, label:"Le mie prenotazioni", anchor:"#prenotazioni" },
    { key:"shifts", icon:Clock, label:"I miei turni", anchor:"#turni" },
    { key:"vehiclecheck", icon:Car, label:"Controllo veicolo", anchor:"#controllo" },
    { key:"conteggi", icon:Coins, label:"I miei conteggi", anchor:"#conteggi" },
    { key:"uber", icon:BarChart3, label:"Storico Uber", anchor:"#uber" },
    { key:"messages", icon:MessageSquare, label:"Messaggi", anchor:"#messaggi" },
    { key:"support", icon:Headphones, label:"Supporto", anchor:"#supporto" },
  ];

  const doLogout = async () => { await logout(); nav("/login"); toast.success("Disconnesso"); };
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(t => t.key === params.get("tab")) ? params.get("tab") : "home";
  const [office, setOffice] = useState(() => params.get("req") ? { id: params.get("req") } : null);
  const go = (key, anchor) => {
    if (key !== tab) setParams(key === "home" ? {} : { tab: key });
    setTimeout(() => window.scrollTo({ top: 0 }), 60);
    if (anchor) [150, 700, 1500, 2600].forEach(ms => setTimeout(() => document.getElementById(anchor)?.scrollIntoView({ behavior: ms < 700 ? "auto" : "smooth", block: "start" }), ms));
  };
  const svcList = useMemo(() => {
    const seen = new Set();
    return [...(data?.bookings || []), ...(data?.bookings_month || [])].filter(b => b?.id && !seen.has(b.id) && seen.add(b.id))
      .sort((a, b) => `${a.date} ${a.time || ""}`.localeCompare(`${b.date} ${b.time || ""}`));
  }, [data]);

  if (!data) return (
    <div className="min-h-screen bg-[#050B18] text-slate-400 flex items-center justify-center">Caricamento area driver…</div>
  );

  return (
    <div className="min-h-screen bg-[#03070F] text-slate-100 flex" data-testid="driver-area">
      <aside className="w-60 shrink-0 hidden lg:flex flex-col bg-[#050C1C] border-r border-[#2F7BFF]/20 sticky top-0 h-screen">
        <div className="px-5 py-6 border-b border-[#2F7BFF]/15"><Brand /></div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {TABS.map(t => (
            <button key={t.key} type="button" onClick={() => go(t.key)} data-testid={`driver-nav-${t.key}`}
              className={`w-full flex items-center gap-3 px-3 min-h-[44px] rounded-lg text-sm font-semibold transition-colors ${tab === t.key ? "bg-[#1F6BFF]/15 text-white border border-[#2F7BFF]/50 shadow-[0_0_14px_rgba(31,107,255,0.25)]" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
              <t.icon size={18} className={tab === t.key ? "text-[#5AA2FF]" : ""} />{t.label}
            </button>
          ))}
        </nav>
      </aside>

      <div className="flex-1 min-w-0" id="top">
        <header className="sticky top-0 z-30 bg-[#03070F]/92 backdrop-blur border-b border-[#2F7BFF]/20 px-4 sm:px-6 py-3 flex items-center justify-between gap-3" style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top, 0px))" }}>
          <div className="lg:hidden"><Brand /></div>
          <div className="hidden lg:block text-sm text-slate-400 capitalize">{fmtDateFull()}</div>
          <div className="flex items-center gap-2">
            <DriverNotificationsBell />
            <button type="button" onClick={() => go("altro")} data-testid="driver-profile-btn" className="w-10 h-10 rounded-full bg-[#0A1630] border border-[#2F7BFF]/40 flex items-center justify-center" aria-label="Profilo">
              <UserIcon size={18} className="text-[#9CC4FF]" />
            </button>
          </div>
        </header>

        <main className="px-4 sm:px-6 pt-4 pb-28 lg:pb-10 space-y-4 max-w-5xl mx-auto" data-testid={`driver-tab-${tab}`}>
          {tab === "home" && (
            <div>
              <h2 className="text-white font-display font-bold text-3xl" data-testid="driver-greeting">{greeting()} {firstName}</h2>
              <div className="text-slate-400 text-sm capitalize">{fmtDateFull()}</div>
            </div>
          )}
          {tab === "home" && <DriverHome data={data} go={go} onOffice={() => setOffice({})} />}
          {office && <DriverOffice initialId={office.id} onClose={() => { setOffice(null); if (params.get("req")) setParams({}); }} />}

          {tab === "servizi" && (
            <div className="space-y-4">
              <ServicesTab bookings={svcList} onOpen={setUpSel} onMonth={() => setShowServicesList(true)} monthCount={(data.bookings_month || []).length} />
              <DriverCopilot />
              <GpsTracker />
              <DriverVehicleCheck />
            </div>
          )}

          {tab === "turni" && <ShiftCalendar />}

          {false && (<>
          {/* Turni + mini calendar (sostituito da ShiftCalendar) */}
          <div id="turni" className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <Card className="xl:col-span-2" title="I miei turni" icon={<Clock className="text-amber-400" size={18}/>}
              right={
                <div className="flex items-center gap-2">
                  <button onClick={()=>setWeekOffset(w=>w-1)} className="text-slate-400 hover:text-white p-1" data-testid="driver-week-prev"><ChevronLeft size={16}/></button>
                  <button onClick={()=>setWeekOffset(0)} className="text-xs text-slate-400 hover:text-amber-400 uppercase font-mono-num tracking-widest">Oggi</button>
                  <button onClick={()=>setWeekOffset(w=>w+1)} className="text-slate-400 hover:text-white p-1" data-testid="driver-week-next"><ChevronRight size={16}/></button>
                  <span className="mx-2 text-slate-700">|</span>
                  <button onClick={()=>setMonthModal(true)} data-testid="driver-open-month" className="text-xs text-amber-400 hover:text-amber-300 inline-flex items-center gap-1 font-medium">Vedi mese completo <ArrowRight size={12}/></button>
                </div>
              }>
              <div className="grid grid-cols-7 gap-1.5">
                {weekDays.map((d, i) => {
                  const s = d.shift?.shift_type;
                  const isToday = d.iso === todayIso;
                  const tone =
                    s === "DAY" ? "border-amber-500/50 bg-amber-500/5" :
                    s === "NIGHT" ? "border-indigo-500/50 bg-indigo-500/5" :
                    s === "REST" ? "border-slate-700 bg-slate-900/40" :
                    "border-slate-800 bg-slate-950/60";
                  const Ic = s === "DAY" ? Sun : s === "NIGHT" ? Moon : Bed;
                  const iconCol = s === "DAY" ? "text-amber-400" : s === "NIGHT" ? "text-indigo-400" : "text-slate-500";
                  const label = s === "DAY" ? "Giorno" : s === "NIGHT" ? "Notte" : s === "REST" ? "Riposo" : "—";
                  return (
                    <div key={i} className={`rounded-xl border p-3 flex flex-col items-center text-center ${tone} ${isToday ? "ring-2 ring-amber-500/60" : ""}`} data-testid={`driver-shift-${i}`}>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">{d.dayLabel}</div>
                      <div className="text-white font-mono-num text-xs mt-0.5">{String(d.date.getDate()).padStart(2,"0")}/{String(d.date.getMonth()+1).padStart(2,"0")}</div>
                      {s ? (<>
                        <Ic size={22} className={`mt-3 ${iconCol}`}/>
                        <div className={`text-xs mt-1.5 font-bold ${iconCol}`}>{label}</div>
                        {(d.shift.time_start || d.shift.time_end) && (
                          <div className="text-[10px] text-slate-500 mt-0.5 font-mono-num">{d.shift.time_start||"--:--"} - {d.shift.time_end||"--:--"}</div>
                        )}
                        {d.shift.vehicle_plate && (
                          <div className="mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 text-[10px] font-mono-num font-extrabold tracking-widest uppercase" data-testid={`driver-shift-plate-${i}`}>
                            <Car size={9}/>{d.shift.vehicle_plate}
                          </div>
                        )}
                      </>) : (
                        <>
                          <div className="mt-3 w-6 h-6 rounded-full border border-slate-700"/>
                          <div className="text-xs text-slate-600 mt-1.5">—</div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-4 mt-4 text-xs text-slate-400">
                <Legend color="bg-amber-400" label="Giorno"/>
                <Legend color="bg-indigo-400" label="Notte"/>
                <Legend color="bg-slate-500" label="Riposo"/>
              </div>
            </Card>

            {/* Mini calendar */}
            <Card title={`${MONTHS_IT[miniCalendar.m]} ${miniCalendar.y}`}
              right={
                <div className="flex items-center gap-1">
                  <button onClick={()=>setCalCursor(d=>{ const n=new Date(d); n.setMonth(n.getMonth()-1); return n; })} className="text-slate-400 hover:text-white p-1"><ChevronLeft size={16}/></button>
                  <button onClick={()=>setCalCursor(d=>{ const n=new Date(d); n.setMonth(n.getMonth()+1); return n; })} className="text-slate-400 hover:text-white p-1"><ChevronRight size={16}/></button>
                </div>
              }>
              <div className="grid grid-cols-7 gap-1 text-center">
                {DAYS_MINI.map(d => <div key={d} className="text-[10px] text-slate-500 uppercase tracking-widest font-mono-num pb-1">{d}</div>)}
                {miniCalendar.cells.map((c, i) => {
                  const inMonth = c.cur;
                  const iso = c.iso;
                  const isToday = iso === todayIso;
                  const hasShift = (data.shifts_month || []).find(s => s.date === iso);
                  const tone = !inMonth ? "text-slate-700" :
                    isToday ? "bg-amber-500 text-slate-950 font-bold rounded-full" :
                    hasShift?.shift_type === "DAY" ? "bg-amber-500/10 text-amber-300 rounded-full" :
                    hasShift?.shift_type === "NIGHT" ? "bg-indigo-500/15 text-indigo-300 rounded-full" :
                    "text-slate-400";
                  return <div key={i} className={`text-xs font-mono-num py-1.5 aspect-square flex items-center justify-center ${tone}`}>{c.day || ""}</div>;
                })}
              </div>
            </Card>
          </div>

          {/* Prenotazioni */}
          <div id="prenotazioni">
            <Card title="I miei servizi" icon={<Calendar className="text-amber-400" size={18}/>}
              right={<a href="#prenotazioni" className="text-xs text-amber-400 hover:text-amber-300 inline-flex items-center gap-1 font-medium">Vedi tutte <ArrowRight size={12}/></a>}>
              <div className="space-y-3" data-testid="driver-services-list">
                {(data.bookings || []).length === 0 && (
                  <div className="py-6 text-center text-slate-500 italic text-sm">Nessuna prenotazione assegnata</div>
                )}
                {(data.bookings || []).map((b, i) => (
                  <DriverServiceCard key={b.id} b={b} onOpen={() => setUpSel(i)} />
                ))}
              </div>
            </Card>
          </div>

          </>)}

          {tab === "uber" && (
            <div id="uber">
              <Card title="Storico Uber" icon={<BarChart3 className="text-amber-400" size={18}/>}
                right={
                  <MonthSelector
                    year={selYear}
                    month={selMonth}
                    availableMonths={data.available_months || []}
                    canGoNext={!isCurrentPeriod}
                    onPrev={goPrevMonth}
                    onNext={goNextMonth}
                    onPick={(y, m) => { setSelYear(y); setSelMonth(m); }}
                  />
                }>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <div className="grid grid-cols-[1fr_auto_auto] gap-3 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num pb-2 border-b border-slate-800">
                      <span>Periodo</span><span>Tipo</span><span className="text-right">Importo</span>
                    </div>
                    <div className="divide-y divide-slate-800/60">
                      {(data.uber_history || []).length === 0 && <div className="text-center text-slate-500 italic py-6 text-sm">Nessuno storico Uber</div>}
                      {(data.uber_history || []).map(u => {
                        const isCash = (u.source || "uber") === "uber_cash";
                        const typeLabel = isCash ? "Uber Cash" : "Uber Normale";
                        const chipCls = isCash
                          ? "border-amber-500/50 text-amber-300 bg-amber-500/10"
                          : "border-sky-500/50 text-sky-300 bg-sky-500/10";
                        const amountCls = isCash ? "text-amber-300" : "text-sky-300";
                        return (
                          <div key={u.id} className="grid grid-cols-[1fr_auto_auto] gap-3 py-2 text-sm items-center" data-testid={`driver-uber-${u.id}`}>
                            <div className="text-slate-200">{formatPeriod(u.period_start, u.period_end)}</div>
                            <span
                              data-testid={`driver-uber-type-${u.id}`}
                              title={isCash ? "Servizi Uber pagati in contanti al driver" : "Servizi Uber pagati elettronicamente"}
                              className={`inline-flex items-center text-[9px] uppercase font-mono-num font-bold tracking-widest px-2 py-0.5 rounded-full border ${chipCls}`}
                            >
                              {typeLabel}
                            </span>
                            <div className={`text-right font-mono-num font-bold ${amountCls}`}>{fmtEUR(u.driver_share || 0)}</div>
                          </div>
                        );
                      })}
                    </div>
                    {(data.uber_history || []).length > 0 && (() => {
                      const totUber = (data.uber_history || []).filter(u => (u.source || "uber") !== "uber_cash").reduce((s,u) => s + (u.driver_share || 0), 0);
                      const totCash = (data.uber_history || []).filter(u => (u.source || "uber") === "uber_cash").reduce((s,u) => s + (u.driver_share || 0), 0);
                      return (
                        <div className="mt-3 pt-3 border-t border-slate-800 grid grid-cols-2 gap-2 text-xs">
                          <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 px-3 py-2" data-testid="driver-uber-total-normale">
                            <div className="text-[9px] uppercase tracking-widest text-sky-300 font-mono-num font-bold">Totale Uber Normale</div>
                            <div className="text-sky-300 font-mono-num font-extrabold text-base mt-0.5">{fmtEUR(totUber)}</div>
                          </div>
                          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2" data-testid="driver-uber-total-cash">
                            <div className="text-[9px] uppercase tracking-widest text-amber-300 font-mono-num font-bold">Totale Uber Cash</div>
                            <div className="text-amber-300 font-mono-num font-extrabold text-base mt-0.5">{fmtEUR(totCash)}</div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 flex items-start gap-3 text-sm text-slate-400">
                    <Info size={18} className="text-sky-400 flex-shrink-0 mt-0.5"/>
                    <div>
                      L'avanzata Uber viene gestita dall'amministrazione. Qui puoi solo visualizzare i tuoi importi, distinti tra <span className="text-sky-300 font-bold">Uber Normale</span> (elettronico) e <span className="text-amber-300 font-bold">Uber Cash</span> (contanti).
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {tab === "conteggi" && (<div className="space-y-4">
            <div id="conteggi">
              <Card title="I miei conteggi" icon={<Coins className="text-amber-400" size={18}/>}
                right={
                  <MonthSelector
                    year={selYear}
                    month={selMonth}
                    availableMonths={data.available_months || []}
                    canGoNext={!isCurrentPeriod}
                    onPrev={goPrevMonth}
                    onNext={goNextMonth}
                    onPick={(y, m) => { setSelYear(y); setSelMonth(m); }}
                  />
                }>
                <div className="space-y-2 text-sm">
                  <Row label="Servizio autista mese" value={fmtEUR(data.accounting.tot_services)}/>
                  <Row label={`Compenso autista ${data.accounting.share_rate}%`} value={fmtEUR(data.accounting.tot_share)}/>

                  {/* UBER NORMALE */}
                  <div className="mt-3 rounded-xl border border-violet-500/30 bg-violet-500/5 px-3 py-2.5" data-testid="section-uber-normale">
                    <div className="text-[10px] uppercase tracking-widest text-violet-300 font-mono-num font-bold mb-1">Uber Normale</div>
                    <Row label="Uber (netto)" value={fmtEUR(data.accounting.uber_net || 0)} hint="totale periodo netto"/>
                    <Row label={`Quota autista ${data.accounting.share_rate}%`} value={fmtEUR(data.accounting.uber || 0)}/>
                  </div>

                  {/* UBER CASH (solo se presente) */}
                  {((data.accounting.uber_cash_gross || 0) > 0 || (data.accounting.uber_cash_driver || 0) > 0) && (
                    <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-3 py-2.5" data-testid="section-uber-cash">
                      <div className="text-[10px] uppercase tracking-widest text-amber-300 font-mono-num font-bold mb-1">Uber Cash</div>
                      <Row label="Uber Cash (lordo)" value={fmtEUR(data.accounting.uber_cash_gross || 0)} hint="cash incassato dal driver"/>
                      <Row label="Quota driver" value={fmtEUR(data.accounting.uber_cash_driver || 0)}/>
                    </div>
                  )}

                  {/* CASH SERVIZI (bookings cash, NON include Uber Cash) */}
                  <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-3 py-2.5" data-testid="section-cash-servizi">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-mono-num font-bold mb-1">Cash Servizi</div>
                    <Row label="Cash normale" value={fmtEUR(data.accounting.cash_services || 0)} hint="solo servizi bookings"/>
                    {data.accounting.services_summary?.cash?.da_versare_azienda > 0 && (
                      <Row label="Da versare azienda" value={fmtEUR(data.accounting.services_summary.cash.da_versare_azienda)} hint="cash in mano − quota driver"/>
                    )}
                  </div>

                  <Row label="Spese" value={fmtEUR(data.accounting.expenses)}/>
                  <div className="border-t border-slate-800 my-2"/>
                  <Row label="Totale Dare" value={fmtEUR(data.accounting.dare)} hint="cash − quota driver + anticipi"/>
                  <Row label="Totale Avere" value={fmtEUR(data.accounting.avere)} hint="solo quote non-cash + Uber + rimborsi"/>
                  <div className="border-t border-slate-800 my-2"/>
                  <Row label="Saldo servizi" value={fmtEUR(data.accounting.saldo_servizi||0)}/>
                  {(data.accounting.movements_avere > 0 || data.accounting.movements_dare > 0) && (
                    <>
                      <Row label="Movimenti Avere" value={fmtEUR(data.accounting.movements_avere||0)} hint="somma spese/mov. AVERE"/>
                      <MovimentiDareRow
                        total={data.accounting.movements_dare || 0}
                        items={data.accounting.movements_dare_items || []}
                      />
                    </>
                  )}
                  <div className={`mt-3 rounded-xl border ${data.accounting.saldo > 0 ? "border-emerald-500/40 bg-emerald-500/5" : data.accounting.saldo < 0 ? "border-rose-500/40 bg-rose-500/5" : "border-slate-500/40 bg-slate-500/5"} p-3 flex items-center justify-between`} data-testid="driver-saldo">
                    <span className="inline-flex items-center gap-2 text-slate-200 font-bold"><Scale size={16} className="text-amber-400"/> Saldo finale netto</span>
                    <span className={`font-mono-num font-extrabold text-lg ${data.accounting.saldo > 0 ? "text-emerald-400" : data.accounting.saldo < 0 ? "text-rose-400" : "text-slate-300"}`}>{fmtEUR(data.accounting.saldo)}</span>
                  </div>
                  <SaldoBadge saldo={data.accounting.saldo}/>
                  <button
                    onClick={()=>setShowServicesList(true)}
                    data-testid="driver-view-services"
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/15 text-amber-300 hover:text-amber-200 text-xs font-bold uppercase tracking-widest font-mono-num transition"
                  >
                    <Receipt size={14}/> Vedi tutti i servizi del periodo ({(data.bookings_month || []).length})
                  </button>
                </div>
              </Card>
            </div>

          {/* Riepilogo per metodo pagamento */}
          {data.accounting.services_summary && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="services-payment-summary">
              <PaymentSummaryCard
                title="Servizi pagati Carta / POS / Bonifico"
                accent="emerald"
                data={data.accounting.services_summary.non_cash}
                shareRate={data.accounting.share_rate}
                target="AVERE"
              />
              <PaymentSummaryCard
                title="Servizi pagati in Contanti"
                accent="amber"
                data={data.accounting.services_summary.cash}
                shareRate={data.accounting.share_rate}
                target="DARE"
                showRestitution
              />
            </div>
          )}
          </div>)}

          {tab === "altro" && (<div className="space-y-4" data-testid="driver-altro">
            <section className="rounded-2xl border border-[#2F7BFF]/25 bg-[#081226] p-4 sm:p-5 flex flex-wrap items-center gap-4" data-testid="driver-profile">
              <div className="w-14 h-14 rounded-full border-2 border-[#2F7BFF]/60 bg-[#0A1630] flex items-center justify-center font-display text-xl text-white">{(data.driver.first_name || "?")[0]}{(data.driver.last_name || "")[0] || ""}</div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-bold text-lg" data-testid="driver-profile-name">{fullName}</div>
                <div className="text-[11px] uppercase tracking-widest text-[#9CC4FF]">Autista {user?.email ? `· ${user.email}` : ""}</div>
                {(data.driver.assigned_vehicles || []).map(v => <div key={v.id} className="text-xs text-[#E9C46A] inline-flex items-center gap-1 mr-3" data-testid={`driver-assigned-vehicle-${v.id}`}><Car size={11} />{[v.brand, v.model].filter(Boolean).join(" ")} · {v.plate}</div>)}
              </div>
              <div className="flex items-center gap-2">
                <ChangePasswordButton />
                <button onClick={doLogout} className="min-h-[44px] px-4 rounded-xl border border-rose-500/50 text-rose-300 text-sm font-bold inline-flex items-center gap-2" data-testid="driver-logout"><LogOut size={15} />Esci</button>
              </div>
            </section>
            <PushNotificationsCard />
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div id="messaggi" className="xl:col-span-2">
              <DriverChatPanel />
            </div>
            <div id="supporto">
              <Card title="Supporto" icon={<Headphones className="text-amber-400" size={18}/>}>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/40 flex items-center justify-center flex-shrink-0">
                    <Headphones className="text-amber-400" size={24}/>
                  </div>
                  <div className="flex-1">
                    <div className="text-slate-300 text-sm">Per qualsiasi necessità contatta l'amministrazione via chat.</div>
                    <button
                      onClick={()=>{
                        document.getElementById("messaggi")?.scrollIntoView({ behavior:"smooth" });
                      }}
                      data-testid="driver-support-btn"
                      className="mt-3 inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-4 py-2 rounded-lg text-sm">
                      Apri chat
                    </button>
                  </div>
                </div>
              </Card>
            </div>
          </div>
          </div>)}
        </main>
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#040A18]/95 backdrop-blur border-t border-[#2F7BFF]/30 grid grid-cols-6" style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }} data-testid="driver-bottom-nav">
          {TABS.map(t => (
            <button key={t.key} type="button" onClick={() => go(t.key)} data-testid={`driver-tab-btn-${t.key}`}
              className={`relative min-h-[58px] flex flex-col items-center justify-center gap-1 text-[10px] font-semibold ${tab === t.key ? "text-[#5AA2FF]" : "text-slate-500"}`}>
              {tab === t.key && <span className="absolute top-0 inset-x-3 h-0.5 rounded-full bg-[#1F6BFF] shadow-[0_0_10px_#1F6BFF]" />}
              <t.icon size={19} />{t.label}
            </button>
          ))}
        </nav>
      </div>
      {monthModal && <MonthModal data={data} onClose={()=>setMonthModal(false)}/>}
      {upSel !== null && svcList[upSel] && (
        <DriverServiceDetailModal
          booking={svcList[upSel]}
          index={upSel}
          total={svcList.length}
          shareRate={data.accounting?.share_rate || 50}
          onClose={() => setUpSel(null)}
          onPrev={() => setUpSel(i => Math.max(0, i - 1))}
          onNext={() => setUpSel(i => Math.min(svcList.length - 1, i + 1))}
        />
      )}
      {showServicesList && (
        <ServicesListModal
          bookings={data.bookings_month || []}
          year={selYear}
          month={selMonth}
          shareRate={data.accounting?.share_rate || 50}
          onClose={()=>setShowServicesList(false)}
        />
      )}
    </div>
  );
}

function MonthModal({ data, onClose }) {
  const [cursor, setCursor] = useState(() => new Date());
  const y = cursor.getFullYear(); const m = cursor.getMonth();
  const first = new Date(y, m, 1);
  const startDow = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(y, m+1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let i = 1; i <= daysInMonth; i++) cells.push(i);
  while (cells.length % 7 !== 0) cells.push(null);
  const todayIso = new Date().toISOString().slice(0,10);
  const isCurrentMonth = (data.accounting.year === y && data.accounting.month === m+1);
  // If different month than data.shifts_month, fetch would be needed; here we only show data available
  const shiftsFor = (day) => {
    if (!day) return null;
    const iso = `${y}-${String(m+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
    return (data.shifts_month || []).find(s => s.date === iso);
  };
  const stats = (data.shifts_month || []).reduce((a, s) => {
    a.total++; a[s.shift_type] = (a[s.shift_type] || 0) + 1; return a;
  }, { total:0 });
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A1428] border border-slate-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e=>e.stopPropagation()} data-testid="driver-month-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <div>
            <h3 className="text-white font-display font-extrabold text-xl">I miei turni · Mese completo</h3>
            <div className="text-xs text-slate-500 mt-0.5">Solo visualizzazione — i turni sono impostati dall'amministrazione</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-2" data-testid="driver-month-close">✕</button>
        </div>
        <div className="px-6 py-3 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={()=>setCursor(d=>{ const n=new Date(d); n.setMonth(n.getMonth()-1); return n; })} className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-slate-800/60"><ChevronLeft size={16}/></button>
            <div className="text-white font-bold text-lg min-w-[180px] text-center">{MONTHS_IT[m]} {y}</div>
            <button onClick={()=>setCursor(d=>{ const n=new Date(d); n.setMonth(n.getMonth()+1); return n; })} className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-slate-800/60"><ChevronRight size={16}/></button>
          </div>
          {isCurrentMonth && (
            <div className="flex items-center gap-4 text-xs text-slate-400">
              <span className="inline-flex items-center gap-1.5"><Sun size={12} className="text-amber-400"/> {stats.DAY || 0} Giorno</span>
              <span className="inline-flex items-center gap-1.5"><Moon size={12} className="text-indigo-400"/> {stats.NIGHT || 0} Notte</span>
              <span className="inline-flex items-center gap-1.5"><Bed size={12} className="text-slate-400"/> {stats.REST || 0} Riposo</span>
            </div>
          )}
        </div>
        <div className="p-6 overflow-y-auto">
          {!isCurrentMonth && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-300 mb-4 text-center">
              Sto visualizzando <b>{MONTHS_IT[m]} {y}</b>. La sincronizzazione dei turni completa avviene automaticamente al ricarico della pagina per il mese corrente.
            </div>
          )}
          <div className="grid grid-cols-7 gap-2">
            {DAYS_SHORT.map(d => <div key={d} className="text-center text-[10px] uppercase tracking-widest text-slate-500 font-mono-num pb-2">{d}</div>)}
            {cells.map((day, i) => {
              if (!day) return <div key={i}/>;
              const s = isCurrentMonth ? shiftsFor(day) : null;
              const iso = `${y}-${String(m+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
              const isToday = iso === todayIso;
              const t = s?.shift_type;
              const tone =
                t === "DAY" ? "border-amber-500/50 bg-amber-500/10" :
                t === "NIGHT" ? "border-indigo-500/50 bg-indigo-500/10" :
                t === "REST" ? "border-slate-700 bg-slate-900/40" :
                "border-slate-800 bg-slate-950/40";
              const Ic = t === "DAY" ? Sun : t === "NIGHT" ? Moon : t === "REST" ? Bed : null;
              const iconCol = t === "DAY" ? "text-amber-400" : t === "NIGHT" ? "text-indigo-400" : "text-slate-500";
              const label = t === "DAY" ? "Giorno" : t === "NIGHT" ? "Notte" : t === "REST" ? "Riposo" : "";
              return (
                <div key={i} className={`rounded-xl border p-3 min-h-[90px] flex flex-col ${tone} ${isToday ? "ring-2 ring-amber-500/60" : ""}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-white font-mono-num font-bold text-sm">{day}</span>
                    {Ic && <Ic size={14} className={iconCol}/>}
                  </div>
                  {label && <div className={`text-xs font-bold mt-2 ${iconCol}`}>{label}</div>}
                  {s?.time_start && <div className="text-[10px] text-slate-400 mt-0.5 font-mono-num">{s.time_start} - {s.time_end || ""}</div>}
                  {s?.vehicle_plate && (
                    <div className="mt-1 inline-flex items-center gap-1 self-start px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 text-[9px] font-mono-num font-extrabold tracking-widest uppercase">
                      <Car size={8}/>{s.vehicle_plate}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, tone }) {
  return (
    <div className="rounded-2xl border border-slate-800/80 bg-[#0A1428] p-5 flex items-center gap-4" data-testid={`driver-kpi-${label.toLowerCase().replace(/\s+/g,"-")}`}>
      <div className="w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-400 flex items-center justify-center flex-shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono-num">{label}</div>
        <div className={`mt-1 font-mono-num font-extrabold text-2xl ${tone==="km" ? "text-white" : "text-white"}`}>{value}</div>
      </div>
    </div>
  );
}

function Card({ title, icon, right, children, className="" }) {
  return (
    <section className={`rounded-2xl border border-[#2F7BFF]/25 bg-[#081226] p-4 sm:p-5 ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="text-white font-display font-extrabold text-lg">{title}</h3>
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function Row({ label, value, hint }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-400">
        {label}
        {hint && <span className="ml-2 text-[10px] text-amber-400/70 font-mono-num uppercase tracking-widest">({hint})</span>}
      </span>
      <span className="text-white font-mono-num font-bold">{value}</span>
    </div>
  );
}

function movimentoIcon(desc) {
  const d = (desc || "").toLowerCase();
  if (/carbur|benzin|gasol|diesel|rifornim/.test(d)) return { Icon: Fuel, tone: "text-amber-400" };
  if (/multa|contravv|sanzion|ztl/.test(d)) return { Icon: AlertTriangle, tone: "text-rose-400" };
  if (/pedagg|autostrad|telepass/.test(d)) return { Icon: Route, tone: "text-sky-400" };
  if (/lavagg|manut|riparaz|officin|gomm/.test(d)) return { Icon: Wrench, tone: "text-emerald-400" };
  return { Icon: Receipt, tone: "text-slate-400" };
}

function MovimentiDareRow({ total, items }) {
  const [open, setOpen] = useState(false);
  const list = Array.isArray(items) ? items : [];
  const has = list.length > 0;
  return (
    <div data-testid="driver-movimenti-dare">
      <button
        type="button"
        onClick={() => has && setOpen(o => !o)}
        disabled={!has}
        className={`w-full flex items-center justify-between text-left rounded-lg px-1 py-1 ${has ? "hover:bg-slate-800/40 cursor-pointer" : "cursor-default"}`}
        data-testid="driver-movimenti-dare-toggle"
      >
        <span className="text-slate-400 inline-flex items-center gap-2">
          Movimenti Dare
          <span className="text-[10px] text-amber-400/70 font-mono-num uppercase tracking-widest">(somma spese/mov. DARE)</span>
          {has && (
            <ChevronDown
              size={14}
              className={`text-amber-400/80 transition-transform ${open ? "rotate-180" : ""}`}
            />
          )}
        </span>
        <span className="text-white font-mono-num font-bold">{fmtEUR(total || 0)}</span>
      </button>
      {open && has && (
        <div
          className="mt-2 rounded-xl border border-amber-500/20 bg-slate-950/60 divide-y divide-slate-800/70 overflow-hidden"
          data-testid="driver-movimenti-dare-details"
        >
          {list.map((m) => {
            const { Icon, tone } = movimentoIcon(m.description);
            const d = m.date ? new Date(m.date + "T00:00:00") : null;
            const dateLabel = d && !isNaN(d.getTime())
              ? `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth()+1).padStart(2, "0")}/${d.getFullYear()}`
              : (m.date || "");
            return (
              <div key={m.id || `${m.date}-${m.description}-${m.amount}`} className="flex items-center gap-3 px-3 py-2.5" data-testid="driver-movimento-dare-item">
                <div className={`w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center flex-shrink-0 ${tone}`}>
                  <Icon size={15}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-slate-200 text-sm truncate">{m.description || "Movimento"}</div>
                  <div className="text-[11px] text-slate-500 font-mono-num">{dateLabel}</div>
                </div>
                <div className="text-amber-300 font-mono-num font-bold text-sm whitespace-nowrap">{fmtEUR(m.amount || 0)}</div>
              </div>
            );
          })}
          <div className="flex items-center justify-between px-3 py-2.5 bg-slate-900/50">
            <span className="text-[10px] uppercase tracking-widest text-slate-400 font-mono-num">Totale Movimenti Dare</span>
            <span className="text-white font-mono-num font-extrabold text-sm">{fmtEUR(total || 0)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function Legend({ color, label }) {
  return <span className="inline-flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-full ${color}`}/>{label}</span>;
}

function formatPeriod(a, b) {
  if (!a || !b) return "-";
  const p = a.split("-"); const q = b.split("-");
  const d1 = `${parseInt(p[2])}`;
  const d2 = `${parseInt(q[2])}`;
  const m = MONTHS_IT[parseInt(p[1])-1]?.slice(0,3) || "";
  return `${d1} – ${d2} ${m} ${p[0]}`;
}
function formatDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
  } catch { return ""; }
}

function SaldoBadge({ saldo }) {
  if (saldo === undefined || saldo === null) return null;
  const s = Number(saldo) || 0;
  if (Math.abs(s) < 0.005) {
    return (
      <div className="mt-2 rounded-lg bg-slate-800/60 border border-slate-700 px-3 py-2 flex items-center gap-2 text-slate-300 text-sm" data-testid="saldo-badge-zero">
        <span>⚪</span><span className="font-bold uppercase tracking-widest text-xs">Nessun saldo</span>
      </div>
    );
  }
  if (s > 0) {
    return (
      <div className="mt-2 rounded-lg bg-emerald-500/10 border border-emerald-500/40 px-3 py-2 flex items-center gap-2 text-emerald-300 text-sm" data-testid="saldo-badge-positive">
        <span>🟢</span><span className="font-bold text-xs uppercase tracking-widest">La società deve dare {fmtEUR(s)} al driver</span>
      </div>
    );
  }
  return (
    <div className="mt-2 rounded-lg bg-rose-500/10 border border-rose-500/40 px-3 py-2 flex items-center gap-2 text-rose-300 text-sm" data-testid="saldo-badge-negative">
      <span>🔵</span><span className="font-bold text-xs uppercase tracking-widest">Il driver deve dare {fmtEUR(Math.abs(s))} alla società</span>
    </div>
  );
}

function PaymentSummaryCard({ title, accent, data, shareRate, target, showRestitution }) {
  const d = data || { count: 0, prezzo_driver_totale: 0, quota_driver: 0, da_versare_azienda: 0 };
  const accentClasses = accent === "emerald"
    ? { border: "border-emerald-500/30", bg: "bg-emerald-500/5", tag: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40", quotaText: "text-emerald-400" }
    : { border: "border-amber-500/30", bg: "bg-amber-500/5", tag: "bg-amber-500/20 text-amber-300 border-amber-500/40", quotaText: "text-amber-400" };
  const tid = accent === "emerald" ? "summary-non-cash" : "summary-cash";
  return (
    <div className={`rounded-2xl border ${accentClasses.border} ${accentClasses.bg} p-4`} data-testid={tid}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <h3 className="font-display font-extrabold text-white text-base leading-tight">{title}</h3>
        <span className={`shrink-0 text-[10px] uppercase font-mono-num font-bold tracking-widest px-2 py-1 rounded-full border ${accentClasses.tag}`}>
          {d.count || 0} serv. → {target}
        </span>
      </div>
      <div className="space-y-1.5 text-sm">
        <div className="flex justify-between items-baseline">
          <span className="text-slate-400">Prezzo driver</span>
          <span className="font-mono-num text-slate-200 font-bold">{fmtEUR(d.prezzo_driver_totale || 0)}</span>
        </div>
        <div className="border-t border-slate-800/50 my-1.5"/>
        <div className="flex justify-between items-baseline">
          <span className="text-slate-300 font-medium">Quota driver {shareRate}%</span>
          <span className={`font-mono-num font-extrabold text-base ${accentClasses.quotaText}`}>{fmtEUR(d.quota_driver || 0)}</span>
        </div>
        {showRestitution && (
          <div className="mt-2 pt-2 border-t border-rose-500/20 flex justify-between items-baseline">
            <span className="text-rose-300 font-medium text-xs uppercase tracking-widest font-mono-num">Da versare azienda</span>
            <span className="font-mono-num font-extrabold text-rose-400 text-base">{fmtEUR(d.da_versare_azienda || 0)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function fmtChatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
  } catch { return ""; }
}

function DriverChatPanel() {
  const [messages, setMessages] = React.useState([]);
  const [body, setBody] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const bottomRef = React.useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/chat/messages");
      setMessages(r.data || []);
      api.post("/chat/mark-read", {}).catch(()=>{});
    } catch { /* noop */ }
  };
  React.useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []);
  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      await api.post("/chat/messages", { body: text });
      setBody("");
      await load();
    } catch {
      toast.error("Invio non riuscito");
    }
    setSending(false);
  };
  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  // group by day
  const grouped = [];
  let lastDay = null;
  for (const m of messages) {
    const day = (m.created_at || "").slice(0, 10);
    if (day !== lastDay) { grouped.push({ type:"day", key:`d-${day}`, iso:m.created_at }); lastDay = day; }
    grouped.push({ type:"msg", key:m.id, msg:m });
  }

  const dayLabel = (iso) => {
    try {
      const d = new Date(iso);
      const now = new Date();
      const y = new Date(now); y.setDate(now.getDate()-1);
      if (d.toDateString() === now.toDateString()) return "Oggi";
      if (d.toDateString() === y.toDateString()) return "Ieri";
      return d.toLocaleDateString("it-IT", { day:"2-digit", month:"long", year:"numeric" });
    } catch { return ""; }
  };

  return (
    <section className="rounded-2xl border border-slate-800/80 bg-[#0A1428] flex flex-col overflow-hidden h-[520px]" data-testid="driver-chat-panel">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800/80">
        <div className="flex items-center gap-2">
          <MessageSquare className="text-amber-400" size={18}/>
          <h3 className="text-white font-display font-extrabold text-lg">Chat con l'ufficio</h3>
        </div>
        <span className="text-[10px] text-slate-500 uppercase tracking-widest font-mono-num">In tempo reale · aggiornamento auto</span>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#050B18]/40" data-testid="driver-chat-list">
        {messages.length === 0 && (
          <div className="text-center text-slate-500 italic py-10 text-sm">Nessun messaggio ancora.<br/>Scrivi al tuo ufficio: risponderà appena possibile.</div>
        )}
        {grouped.map(item => {
          if (item.type === "day") {
            return (
              <div key={item.key} className="flex justify-center my-2">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num bg-slate-900/60 border border-slate-800 rounded-full px-3 py-0.5">{dayLabel(item.iso)}</span>
              </div>
            );
          }
          const m = item.msg;
          const mine = m.sender_role === "autista";
          return (
            <div key={item.key} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`driver-chat-msg-${m.id}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${mine ? "bg-amber-500 text-slate-950" : "bg-slate-800 text-slate-100 border border-slate-700"}`}>
                {!mine && (<div className="text-[10px] font-mono-num uppercase tracking-widest opacity-70 mb-0.5">{m.sender_name || "Amministrazione"}</div>)}
                <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">{m.body}</div>
                <div className={`flex items-center gap-1 justify-end mt-1 text-[10px] font-mono-num ${mine ? "text-slate-900/70" : "text-slate-500"}`}>
                  {fmtChatTime(m.created_at)}
                  {mine && (m.read_by_office ? <CheckCheck size={12}/> : <Check size={12}/>)}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef}/>
      </div>
      <div className="p-3 border-t border-slate-800/80">
        <div className="flex items-end gap-2">
          <textarea
            value={body}
            onChange={e=>setBody(e.target.value)}
            onKeyDown={onKey}
            rows={2}
            placeholder="Scrivi all'ufficio... (Invio per inviare)"
            data-testid="driver-chat-composer"
            className="flex-1 bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-amber-500/60 resize-none"
          />
          <button
            onClick={send}
            disabled={sending || !body.trim()}
            data-testid="driver-chat-send"
            className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-slate-950 font-bold h-11 px-4 rounded-lg text-sm">
            <Send size={14}/> Invia
          </button>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   MonthSelector — Storico mensile driver (iter 18)
   ============================================================ */
function MonthSelector({ year, month, availableMonths, canGoNext, onPrev, onNext, onPick }) {
  const [open, setOpen] = useState(false);
  const label = `${MONTHS_IT[month-1]} ${year}`;
  const items = (availableMonths || []).map(s => {
    const [y, m] = s.split("-").map(Number);
    return { key: s, year: y, month: m, label: `${MONTHS_IT[m-1]} ${y}` };
  });
  return (
    <div className="relative inline-flex items-center gap-1 rounded-xl border border-amber-500/40 bg-black/60 px-1 py-1" data-testid="driver-month-selector">
      <button onClick={onPrev} data-testid="driver-month-prev" className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-amber-400 hover:bg-amber-500/15 hover:text-amber-300 transition" title="Mese precedente">
        <ChevronLeft size={16}/>
      </button>
      <button onClick={() => setOpen(o => !o)} data-testid="driver-month-current" className="px-3 py-1.5 min-w-[140px] text-amber-300 hover:text-amber-200 text-xs font-bold uppercase tracking-widest font-mono-num inline-flex items-center justify-center gap-1.5">
        {label} <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      <button onClick={onNext} disabled={!canGoNext} data-testid="driver-month-next" className={`w-8 h-8 inline-flex items-center justify-center rounded-lg transition ${canGoNext ? "text-amber-400 hover:bg-amber-500/15 hover:text-amber-300" : "text-slate-700 cursor-not-allowed"}`} title="Mese successivo">
        <ChevronRight size={16}/>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)}/>
          <div className="absolute top-full right-0 mt-2 z-40 w-56 max-h-72 overflow-y-auto rounded-xl border border-amber-500/40 bg-[#0A0A0A] shadow-2xl shadow-black/90 py-1.5" data-testid="driver-month-dropdown">
            {items.length === 0 ? (
              <div className="px-3 py-2 text-xs text-slate-500 italic">Nessuno storico disponibile</div>
            ) : items.map(it => {
              const active = it.year === year && it.month === month;
              return (
                <button key={it.key} onClick={() => { onPick(it.year, it.month); setOpen(false); }} data-testid={`driver-month-opt-${it.key}`} className={`w-full text-left px-3 py-2 text-xs font-mono-num font-bold uppercase tracking-widest transition ${active ? "bg-amber-500/15 text-amber-300" : "text-slate-300 hover:bg-amber-500/5 hover:text-amber-300"}`}>
                  {it.label}{active && <span className="float-right text-amber-400">●</span>}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* ============================================================
   ServicesListModal — Elenco completo servizi del periodo
   ============================================================ */
function ServicesListModal({ bookings, year, month, shareRate = 50, onClose }) {
  const label = `${MONTHS_IT[month-1]} ${year}`;
  const [selectedIdx, setSelectedIdx] = React.useState(null);
  const selected = selectedIdx !== null ? bookings[selectedIdx] : null;

  // ESC closes nested detail first, then list
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (selectedIdx !== null) setSelectedIdx(null);
        else onClose();
      } else if (selectedIdx !== null) {
        if (e.key === "ArrowRight" && selectedIdx < bookings.length - 1) setSelectedIdx(i => i + 1);
        else if (e.key === "ArrowLeft" && selectedIdx > 0) setSelectedIdx(i => i - 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedIdx, bookings.length, onClose]);

  const pmBadge = (pm) => {
    const v = (pm || "").toLowerCase();
    if (v.includes("uber") && v.includes("cash")) return { label: "Uber Cash", cls: "border-amber-500/50 text-amber-300 bg-amber-500/10" };
    if (v.includes("uber")) return { label: "Uber", cls: "border-violet-500/50 text-violet-300 bg-violet-500/10" };
    if (v === "cash" || v === "contanti" || v === "contante") return { label: "Cash", cls: "border-emerald-500/50 text-emerald-300 bg-emerald-500/10" };
    if (v === "pos") return { label: "POS", cls: "border-sky-500/50 text-sky-300 bg-sky-500/10" };
    if (v === "carta" || v === "card") return { label: "Carta", cls: "border-sky-500/50 text-sky-300 bg-sky-500/10" };
    if (v === "bonifico") return { label: "Bonifico", cls: "border-indigo-500/50 text-indigo-300 bg-indigo-500/10" };
    return { label: pm || "—", cls: "border-slate-500/50 text-slate-300 bg-slate-500/10" };
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-start justify-center p-0 sm:p-4 overflow-y-auto" onClick={onClose} data-testid="driver-services-list">
      <div className="w-full max-w-4xl bg-[#030303] border border-amber-500/40 rounded-none sm:rounded-2xl my-0 sm:my-6"
           style={{ boxShadow: "0 0 0 1px rgba(214,166,42,0.22), 0 24px 70px -16px rgba(214,166,42,0.32), 0 50px 120px -24px rgba(0,0,0,0.95)" }}
           onClick={e => e.stopPropagation()}>
        <div className="px-4 sm:px-6 py-4 border-b border-amber-500/20 flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-400 font-mono-num">Riepilogo servizi</div>
            <div className="text-white font-display text-xl sm:text-2xl font-extrabold">{label.toUpperCase()}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">{bookings.length} servizi · Clicca una riga per il dettaglio</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-800/60" data-testid="driver-services-close">✕</button>
        </div>
        {bookings.length === 0 ? (
          <div className="p-10 text-center text-slate-500 italic">Nessun servizio in questo periodo</div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto max-h-[70vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="text-[9px] uppercase tracking-widest text-slate-500 font-mono-num bg-slate-950/80 sticky top-0 z-10">
                  <tr className="border-b border-amber-500/20">
                    <th className="text-left px-4 py-2.5">Data</th>
                    <th className="text-left px-2 py-2.5">Ora</th>
                    <th className="text-left px-2 py-2.5">Percorso</th>
                    <th className="text-left px-2 py-2.5">Pagamento</th>
                    <th className="text-right px-2 py-2.5">Prezzo Driver</th>
                    <th className="text-right px-2 py-2.5">Quota {shareRate}%</th>
                    <th className="text-left px-2 py-2.5">Stato</th>
                    <th className="w-6 px-2 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/40">
                  {bookings.map((b, i) => {
                    const dp = Number(b.driver_price ?? 0);
                    const qt = Number(b.driver_share_calc ?? 0);
                    const pm = pmBadge(b.payment_method);
                    const st = STATUS_STYLE[b.status] || { dot: "bg-slate-500", label: b.status || "—" };
                    return (
                      <tr
                        key={b.id || i}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedIdx(i)}
                        onKeyDown={e => (e.key === "Enter" || e.key === " ") && setSelectedIdx(i)}
                        className="group cursor-pointer hover:bg-amber-500/5 focus:bg-amber-500/10 focus:outline-none transition-colors"
                        data-testid={`driver-service-row-${i}`}
                      >
                        <td className="px-4 py-2.5 font-mono-num text-slate-300 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <span>{(b.date || "").slice(5).replace("-","/")}</span>
                            {b.driver_modified_after_view && (
                              <span
                                data-testid={`driver-service-modified-badge-${i}`}
                                title="Aggiornato dopo la tua visualizzazione"
                                className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-mono-num font-bold text-orange-200 bg-orange-500/15 border border-orange-500/50 rounded px-1.5 py-0.5 animate-pulse"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-orange-400"/> Aggiornato
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2.5 font-mono-num text-amber-400 whitespace-nowrap">{b.time || "-"}</td>
                        <td className="px-2 py-2.5 text-slate-200 max-w-[280px] truncate">{(b.pickup || "—")} <span className="text-slate-600">→</span> {(b.destination || "—")}</td>
                        <td className="px-2 py-2.5"><span className={`inline-flex items-center text-[9px] uppercase font-mono-num font-bold tracking-widest px-2 py-0.5 rounded-full border ${pm.cls}`}>{pm.label}</span></td>
                        <td className="px-2 py-2.5 text-right font-mono-num text-slate-200 font-bold whitespace-nowrap">{fmtEUR(dp)}</td>
                        <td className="px-2 py-2.5 text-right font-mono-num text-emerald-400 font-extrabold whitespace-nowrap">{fmtEUR(qt)}</td>
                        <td className="px-2 py-2.5"><span className="inline-flex items-center gap-1.5 text-[10px] text-slate-300 font-mono-num"><span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}/>{st.label}</span></td>
                        <td className="px-2 py-2.5 text-amber-500/40 group-hover:text-amber-400 transition"><ArrowRight size={14}/></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-slate-800/60 max-h-[70vh] overflow-y-auto">
              {bookings.map((b, i) => {
                const dp = Number(b.driver_price ?? 0);
                const qt = Number(b.driver_share_calc ?? 0);
                const pm = pmBadge(b.payment_method);
                return (
                  <button
                    key={b.id || i}
                    onClick={() => setSelectedIdx(i)}
                    className="w-full text-left p-3.5 hover:bg-amber-500/5 active:bg-amber-500/10 transition cursor-pointer"
                    data-testid={`driver-service-card-${i}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <div className="font-mono-num text-amber-400 text-xs font-bold">{(b.date || "").slice(5).replace("-","/")} · {b.time || ""}</div>
                        {b.driver_modified_after_view && (
                          <span
                            data-testid={`driver-service-modified-badge-mobile-${i}`}
                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-mono-num font-bold text-orange-200 bg-orange-500/15 border border-orange-500/50 rounded px-1.5 py-0.5 animate-pulse"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-400"/> Aggiornato
                          </span>
                        )}
                      </div>
                      <ArrowRight size={14} className="text-amber-500/60 flex-shrink-0 mt-0.5"/>
                    </div>
                    <div className="text-slate-200 text-sm truncate">{b.pickup || "—"} <span className="text-slate-600">→</span> {b.destination || "—"}</div>
                    <div className="flex items-center justify-between gap-2 mt-2">
                      <span className={`inline-flex items-center text-[9px] uppercase font-mono-num font-bold tracking-widest px-2 py-0.5 rounded-full border ${pm.cls}`}>{pm.label}</span>
                      <div className="text-right">
                        <div className="font-mono-num text-slate-200 text-sm">{fmtEUR(dp)}</div>
                        <div className="font-mono-num text-emerald-400 text-xs font-extrabold">{fmtEUR(qt)}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {selected && (
        <DriverServiceDetailModal
          booking={selected}
          index={selectedIdx}
          total={bookings.length}
          shareRate={shareRate}
          onClose={() => setSelectedIdx(null)}
          onPrev={() => setSelectedIdx(i => Math.max(0, i - 1))}
          onNext={() => setSelectedIdx(i => Math.min(bookings.length - 1, i + 1))}
        />
      )}
    </div>
  );
}

function DriverServiceDetailModal({ booking: b, index, total, shareRate, onClose, onPrev, onNext }) {
  const dp = Number(b.driver_price ?? 0);
  const qt = Number(b.driver_share_calc ?? 0);
  React.useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape" && !e.defaultPrevented) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [onClose]);
  // (iter 34) Mark-viewed: registra la visualizzazione quando il driver apre il Dettaglio.
  // (iter 35) Richiama anche quando il servizio è stato modificato dopo la visualizzazione,
  // così il backend azzera il flag "driver_modified_after_view" (il driver ha riletto).
  React.useEffect(() => {
    if (!b?.id) return;
    if (b.driver_viewed === true && !b.driver_modified_after_view) return;
    let cancelled = false;
    (async () => {
      try {
        await api.post(`/bookings/${b.id}/mark-viewed`);
        if (!cancelled) {
          b.driver_viewed = true;
          b.driver_modified_after_view = false;
          b.driver_modified_fields = [];
        }
      } catch (_) { /* silenzioso: non interrompere l'esperienza del driver */ }
    })();
    return () => { cancelled = true; };
  }, [b?.id]);
  const [statusOverride, setStatusOverride] = React.useState(null);
  React.useEffect(() => { setStatusOverride(null); }, [b?.id]);
  const st = STATUS_STYLE[statusOverride || b.status] || { dot: "bg-slate-500", label: statusOverride || b.status || "—" };
  const pmRaw = (b.payment_method || "").toLowerCase();
  const paymentLabel =
    pmRaw.includes("uber") && pmRaw.includes("cash") ? "Uber Cash" :
    pmRaw.includes("uber") ? "Uber (normale)" :
    pmRaw === "cash" || pmRaw === "contanti" || pmRaw === "contante" ? "Cash" :
    pmRaw === "pos" ? "POS" :
    pmRaw === "carta" || pmRaw === "card" ? "Carta" :
    pmRaw === "bonifico" ? "Bonifico" :
    pmRaw === "altro" ? "Altro" :
    pmRaw === "buono" ? "Buono" :
    (b.payment_method || "—");
  const dateFmt = (() => {
    try {
      if (!b.date) return "—";
      const [y, m, d] = b.date.split("-");
      return `${d}/${m}/${y}`;
    } catch { return b.date || "—"; }
  })();
  const travelNo = (b.flight_number || "").trim();
  const isTrain = /treno|stazione|ferrov|train/i.test(`${b.service_type || ""} ${b.pickup || ""} ${b.destination || ""}`)
    && !/aeroport|airport|volo/i.test(b.service_type || "");
  const mapsUrl = (b.pickup && b.destination)
    ? `https://www.google.com/maps/dir/${encodeURIComponent(b.pickup)}/${encodeURIComponent(b.destination)}`
    : null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-stretch sm:items-start justify-center p-0 sm:p-4 sm:overflow-y-auto" onClick={onClose} data-testid="driver-service-detail">
      <div
        className="svc-sheet w-full max-w-2xl bg-[#060606] border border-amber-500/50 rounded-none sm:rounded-2xl my-0 sm:my-6 flex flex-col overflow-hidden"
        style={{ boxShadow: "0 0 0 1px rgba(245,203,92,0.3), 0 32px 80px -20px rgba(245,203,92,0.4), 0 60px 140px -30px rgba(0,0,0,0.98)" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="svc-sheet-header sticky top-0 z-20 shrink-0 px-5 sm:px-6 py-4 border-b border-amber-500/20 flex items-start justify-between gap-3 bg-[#060606] bg-gradient-to-r from-amber-500/10 via-transparent to-transparent">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-400 font-mono-num font-bold">Dettaglio Servizio</div>
            <div className="text-white font-display text-xl sm:text-2xl font-extrabold mt-0.5">{b.service_type || "Servizio NCC"}</div>
            {b.booking_number && <div className="text-[10px] text-slate-500 font-mono-num mt-0.5">#{b.booking_number}</div>}
          </div>
          <button type="button" onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Chiudi scheda" className="text-slate-300 hover:text-white w-11 h-11 -mr-2 -mt-1 inline-flex items-center justify-center rounded-full border border-slate-700/70 bg-slate-900/80 hover:bg-slate-800 flex-shrink-0 text-lg" data-testid="driver-service-detail-close">✕</button>
        </div>

        <div className="p-5 sm:p-6 space-y-5 flex-1 min-h-0 overflow-y-auto overscroll-contain sm:max-h-[75vh]" data-testid="driver-service-detail-body">
          {/* (iter 35) Alert modifica post-visualizzazione */}
          {b.driver_modified_after_view && (
            <div
              data-testid="driver-detail-modified-alert"
              className="rounded-xl border-2 border-orange-500/60 bg-gradient-to-br from-orange-500/15 to-orange-500/5 p-4 flex items-start gap-3"
            >
              <AlertTriangle size={22} className="text-orange-400 flex-shrink-0 mt-0.5"/>
              <div className="flex-1 min-w-0">
                <div className="text-orange-300 font-display font-extrabold text-base">Servizio aggiornato dall'ufficio</div>
                <div className="text-orange-200/90 text-xs mt-1">
                  Alcuni dettagli operativi sono stati modificati dopo la tua visualizzazione. Controllali attentamente.
                </div>
                {Array.isArray(b.driver_modified_fields) && b.driver_modified_fields.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {b.driver_modified_fields.map((f, i) => {
                      const LBL = { date:"Data", time:"Orario", pickup:"Partenza", destination:"Destinazione", vehicle_id:"Veicolo", notes:"Note", stops:"Fermate" };
                      return (
                        <span key={i} className="inline-flex items-center text-[10px] uppercase tracking-widest font-mono-num font-bold text-orange-200 bg-orange-500/20 border border-orange-500/50 rounded px-1.5 py-0.5">
                          {LBL[f] || f}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Data / Ora / Stato */}
          <div className="grid grid-cols-3 gap-3">
            <DetailCell icon={Calendar} label="Data" value={dateFmt}/>
            <DetailCell icon={Clock}    label="Ora"  value={b.time || "—"}/>
            <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-3">
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono-num mb-1">Stato</div>
              <div className="inline-flex items-center gap-2 text-sm text-slate-200 font-bold">
                <span className={`w-2 h-2 rounded-full ${st.dot}`}/>
                {st.label}
              </div>
            </div>
          </div>

          {travelNo && (
            <div data-testid="driver-service-detail-travel-no">
              <DetailCell icon={isTrain ? TrainFront : Plane} label={isTrain ? "N° Treno" : "N° Volo"} value={travelNo} valueMono/>
            </div>
          )}

          {/* Percorso */}
          {(b.pickup || b.destination) && (
            <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
              <div className="text-[9px] uppercase tracking-widest text-amber-400 font-mono-num font-bold mb-2">Percorso</div>
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <div className="w-6 flex items-center justify-center pt-0.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-emerald-500/30"/></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono-num">Partenza</div>
                    <div className="text-slate-200 text-sm">{b.pickup || "—"}</div>
                  </div>
                </div>
                <div className="w-6 flex justify-center"><div className="w-0.5 h-5 bg-amber-500/40"/></div>
                <div className="flex items-start gap-3">
                  <div className="w-6 flex items-center justify-center pt-0.5"><span className="w-2.5 h-2.5 rounded-full bg-rose-400 ring-2 ring-rose-500/30"/></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono-num">Destinazione</div>
                    <div className="text-slate-200 text-sm">{b.destination || "—"}</div>
                  </div>
                </div>
              </div>
              {mapsUrl && (
                <a href={mapsUrl} target="_blank" rel="noreferrer" data-testid="driver-service-detail-maps"
                   className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono-num font-bold text-amber-300 hover:text-amber-200">
                  <Route size={12}/> Apri su Google Maps
                </a>
              )}
            </div>
          )}

          {/* Tappe intermedie */}
          {b.stops && (
            <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4" data-testid="driver-service-detail-stops">
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono-num mb-1">Tappe intermedie</div>
              <div className="text-slate-200 text-sm whitespace-pre-wrap break-words">{b.stops}</div>
            </div>
          )}

          {/* Passeggeri / Bagagli (iter 37: Cliente rimosso per privacy driver) */}
          <div className="grid grid-cols-2 gap-3">
            <DetailCell icon={UserIcon} label="Passeggeri"  value={String(b.passengers || 1)}/>
            <DetailCell icon={Receipt}  label="Bagagli"     value={String(b.luggage || 0)}/>
          </div>

          {/* (iter 38) Passeggero + Telefono passeggero — dati operativi per il driver */}
          {(b.passenger_name || b.phone) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <DetailCell icon={UserIcon} label="Passeggero" value={b.passenger_name || "—"}/>
              <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-3">
                <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono-num mb-1 inline-flex items-center gap-1">
                  <Phone size={10} className="text-amber-400/80"/> Telefono passeggero
                </div>
                {b.phone ? (
                  <a
                    href={`tel:${b.phone}`}
                    data-testid="driver-service-detail-passenger-phone"
                    className="text-amber-300 hover:text-amber-200 text-sm font-bold font-mono-num tracking-wide underline underline-offset-2 decoration-amber-500/40"
                  >
                    {b.phone}
                  </a>
                ) : (
                  <div className="text-slate-500 text-sm italic">Telefono non disponibile</div>
                )}
              </div>
            </div>
          )}

          {/* Veicolo / Targa */}
          {(b._vehicle_name || b._vehicle_plate) && (
            <div className="grid grid-cols-2 gap-3">
              <DetailCell icon={Car} label="Veicolo" value={b._vehicle_name || "—"}/>
              <DetailCell label="Targa" valueMono value={b._vehicle_plate || "—"}/>
            </div>
          )}

          {/* Pagamento */}
          <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono-num mb-1">Pagamento</div>
            <div className="text-slate-200 text-base font-bold">{paymentLabel}</div>
          </div>

          {isDispo(b) && <DispoDriverPanel key={`dispo-${b.id}`} booking={b} shareRate={shareRate} onChanged={(nb) => setStatusOverride(nb.status)} />}

          {/* Il tuo compenso */}
          {!isDispo(b) && <div className="rounded-xl border border-amber-500/40 bg-gradient-to-br from-amber-500/15 to-transparent p-4" data-testid="driver-service-detail-compensation">
            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-400 font-mono-num font-bold mb-3 inline-flex items-center gap-1.5"><Coins size={12}/> Il tuo compenso</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-slate-950/70 border border-slate-800 p-3">
                <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono-num">Prezzo Driver</div>
                <div className="text-slate-100 font-mono-num font-extrabold text-xl mt-1">{fmtEUR(dp)}</div>
              </div>
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/40 p-3">
                <div className="text-[9px] uppercase tracking-widest text-emerald-300 font-mono-num">Quota Driver {shareRate}%</div>
                <div className="text-emerald-300 font-mono-num font-extrabold text-xl mt-1">{fmtEUR(qt)}</div>
              </div>
            </div>
          </div>}

          {/* Note */}
          <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4 space-y-3" data-testid="driver-service-detail-notes">
            <div>
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono-num mb-1">Note operative</div>
              <div className="text-slate-200 text-sm whitespace-pre-wrap break-words">{b.notes || "Nessuna nota"}</div>
            </div>
            {b.route_notes && (
              <div>
                <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono-num mb-1">Note percorso</div>
                <div className="text-slate-200 text-sm whitespace-pre-wrap break-words">{b.route_notes}</div>
              </div>
            )}
          </div>
          {b.id && !isDispo(b) && <ServiceProgressPanel key={b.id} bookingId={b.id} />}
        </div>

        {/* Nav footer */}
        <div className="svc-sheet-footer shrink-0 px-5 sm:px-6 py-3 border-t border-amber-500/20 flex items-center justify-between gap-3 bg-slate-950">
          <button
            onClick={onPrev}
            disabled={index <= 0}
            data-testid="driver-service-detail-prev"
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest font-mono-num px-3 py-1.5 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={14}/> Precedente
          </button>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
            Servizio {index + 1} di {total}
          </div>
          <button
            onClick={onNext}
            disabled={index >= total - 1}
            data-testid="driver-service-detail-next"
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest font-mono-num px-3 py-1.5 rounded-lg border border-amber-500/40 text-amber-300 hover:bg-amber-500/15 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Successivo <ChevronRight size={14}/>
          </button>
        </div>
      </div>
    </div>
  );
}

function DriverServiceCard({ b, onOpen }) {
  const st = STATUS_STYLE[b.status] || { dot: "bg-slate-400", label: b.status || "—" };
  const [, m, d] = (b.date || "").split("-");
  const mon = m ? ["GEN","FEB","MAR","APR","MAG","GIU","LUG","AGO","SET","OTT","NOV","DIC"][Number(m) - 1] : "";
  const veh = b._vehicle_name && b._vehicle_name !== "-" ? b._vehicle_name : null;
  return (
    <button type="button" onClick={onOpen} data-testid={`driver-booking-${b.id}`}
      className="w-full text-left flex rounded-2xl border border-amber-500/30 bg-gradient-to-br from-[#0b0a08] to-black hover:border-amber-400/70 transition-colors overflow-hidden">
      <div className="w-20 sm:w-24 shrink-0 flex flex-col items-center justify-center border-r border-amber-500/25 py-4 bg-amber-500/[0.04]">
        <div className="text-white font-mono-num font-extrabold text-xl sm:text-2xl leading-none" data-testid="driver-booking-time">{b.time || "--:--"}</div>
        <div className="text-amber-400 font-mono-num text-xs font-bold mt-1.5 tracking-widest">{d ? `${d} ${mon}` : "—"}</div>
      </div>
      <div className="flex-1 min-w-0 p-3 sm:p-4 space-y-1.5">
        <div className="flex items-start justify-between gap-2">
          <div className="text-white font-semibold text-sm sm:text-base truncate">{b.service_type || "Servizio NCC"}</div>
          <span className="shrink-0 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono-num font-bold text-slate-200 border border-slate-700 rounded-full px-2 py-0.5" data-testid="driver-booking-status">
            <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}/>{st.label}
          </span>
        </div>
        <div className="text-xs sm:text-sm text-slate-300 flex items-start gap-1.5"><span className="mt-1 w-2 h-2 rounded-full bg-emerald-400 shrink-0"/><span className="line-clamp-1">{b.pickup || "—"}</span></div>
        <div className="text-xs sm:text-sm text-slate-300 flex items-start gap-1.5"><span className="mt-1 w-2 h-2 rounded-full bg-rose-400 shrink-0"/><span className="line-clamp-1">{b.destination || "—"}</span></div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-1 text-xs text-slate-400">
          <span className="inline-flex items-center gap-1"><UserIcon size={12} className="text-amber-400"/>{b.passengers || 1} pax</span>
          <span className="inline-flex items-center gap-1"><Receipt size={12} className="text-amber-400"/>{b.luggage || 0} bagagli</span>
          {b.passenger_name && <span className="text-slate-300 truncate max-w-[10rem]">{b.passenger_name}</span>}
          {veh && <span className="inline-flex items-center gap-1 text-slate-300"><Car size={12} className="text-amber-400"/>{veh}{b._vehicle_plate ? ` · ${b._vehicle_plate}` : ""}</span>}
        </div>
        {(b.notes || b.route_notes) && (
          <div className="text-xs text-amber-100/90 whitespace-pre-wrap break-words border-t border-amber-500/15 pt-1.5 mt-1" data-testid="driver-booking-notes">
            <span className="text-amber-400 font-semibold">Note: </span>{[b.notes, b.route_notes].filter(Boolean).join("\n")}
          </div>
        )}
      </div>
      <div className="flex items-center pr-2 text-amber-400"><ChevronRight size={18}/></div>
    </button>
  );
}

function DetailCell({ icon: Icon, label, value, valueMono = false }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-3">
      <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono-num mb-1 inline-flex items-center gap-1">
        {Icon && <Icon size={10} className="text-amber-400/80"/>} {label}
      </div>
      <div className={`text-slate-200 text-sm font-bold truncate ${valueMono ? "font-mono-num text-amber-300" : ""}`} title={value}>{value}</div>
    </div>
  );
}
